<script setup lang="ts">
import { getSmallestThumbnailUrl, getTranslatedProperty } from "@shopware-pwa/helpers-next";
import type { Product } from "@shopware-pwa/types";
import { Divider } from "~/components/clarity-ui/Divider";
import type { EshopSetConfiguratorStepProduct } from "~/composables/shop/eShopSetConfiguratorTypes";
import ProductImage from "~/templates/elements/ProductImage.vue";
import { getValueSign } from "~/utils/helper/number";
import { Heading } from "~/components/clarity-ui/Heading";

export interface ProductFullOptionProps {
  stepProduct: EshopSetConfiguratorStepProduct;
}

const { stepProduct } = defineProps<ProductFullOptionProps>();

const product = computed<Product>(() => stepProduct?.product as Product);
const title = computed(() => getTranslatedProperty(product.value, "name"));
const productShortDescriptionEshopStep = computed(() =>
  getTranslatedProperty(
    product?.value?.translated?.customFields,
    "brita_product_short_description_eshop_step",
  ),
);
const productLongDescriptionEshopStep = computed(() => stepProduct?.description);
const absPriceDifference = computed(() => Math.abs(stepProduct?.differencePrice ?? 0));
const priceSign = computed(() => getValueSign(stepProduct?.differencePrice));
const mainImage = computed(() => getSmallestThumbnailUrl(product?.value?.cover?.media));
</script>

<template>
  <div class="relative flex w-full flex-col gap-4 p-4 text-left lg:py-6">
    <ProductBadge :product="product" class="absolute right-4 top-0" />
    <div :class="{ '-ml-4': mainImage }" class="flex items-center gap-4">
      <div v-if="mainImage" class="w-18 shrink-0">
        <div class="aspect-square">
          <ProductImage
            :product-media="product?.cover?.media"
            main-class="object-contain w-full h-full"
          />
        </div>
      </div>
      <div class="flex-1">
        <div class="flex justify-between gap-0.5">
          <Heading
            tag="h4"
            size="small"
          >
            {{ title }}
          </Heading>
          <div
            v-if="absPriceDifference && !stepProduct?.selected"
            class="inline-flex shrink-0 items-center gap-1 self-start text-sm font-normal leading-5 text-black"
          >
            <SharedPrice :value="absPriceDifference">
              <template #beforePrice>{{ priceSign }}</template>
            </SharedPrice>
          </div>
        </div>
        <div
          class="flex-none text-sm font-normal leading-5 text-black"
          v-html="productShortDescriptionEshopStep"
        />
      </div>
    </div>
    <template v-if="productLongDescriptionEshopStep">
      <Divider />
      <div class="prose" v-html="productLongDescriptionEshopStep" />
    </template>
  </div>
</template>
